import React, { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useFetch } from "../lib/hooks";
import { fileUrl } from "../lib/api";
import { Reveal } from "../components/site/Reveal";
import PageHeader from "../components/site/PageHeader";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { X } from "lucide-react";

const CATS = ["ALL", "FOOD", "EVENTS", "BUFFETS", "WEDDINGS", "LIVE COUNTERS", "CORPORATE", "HOSPITALITY"];

export default function Gallery() {
  const { data: items } = useFetch("/public/gallery");
  const [params] = useSearchParams();
  const [cat, setCat] = useState(params.get("cat") || "ALL");
  const [light, setLight] = useState(null);

  const filtered = useMemo(() => cat === "ALL" ? items : items.filter((i) => i.category === cat), [items, cat]);
  const spanFor = (shape, i) => {
    if (shape === "landscape") return "sm:col-span-2";
    if (shape === "portrait") return "sm:row-span-2";
    return "";
  };

  return (
    <div>
      <PageHeader overline="The Gallery" title={<>Moments, <span className="italic gold-text">plated.</span></>}
        subtitle="An editorial look at our food, events and hospitality." image={items[0]?.image} />

      <section className="py-16 sm:py-24 bg-ivory">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8">
          <div className="flex gap-2.5 flex-wrap justify-center mb-12">
            {CATS.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`gallery-filter-${c.toLowerCase().replace(/ /g, "-")}`}
                className={`px-5 py-2.5 rounded-full text-xs tracking-widest uppercase border transition-all ${cat === c ? "bg-burgundy text-ivory border-burgundy" : "border-gold/30 text-charcoal/60 hover:border-gold"}`}>
                {c}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 auto-rows-[180px] sm:auto-rows-[220px]">
            {filtered.map((g, i) => (
              <Reveal key={g.id} delay={(i % 4) * 0.05} className={spanFor(g.shape, i)}>
                <button onClick={() => setLight(g)} data-testid={`gallery-item-${i}`} className="group block w-full h-full overflow-hidden rounded-2xl relative">
                  <img src={fileUrl(g.image)} alt={g.name} className="w-full h-full object-cover img-zoom" />
                  <div className="absolute inset-0 bg-gradient-to-t from-velvet/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                    <div><span className="text-gold text-[0.6rem] tracking-widest">{g.category}</span><p className="text-ivory font-serif text-lg leading-tight">{g.name}</p></div>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
          {!filtered.length && <p className="text-center text-charcoal/40 py-20">No items in this category yet.</p>}
        </div>
      </section>

      <Dialog open={!!light} onOpenChange={(o) => !o && setLight(null)}>
        <DialogContent className="max-w-4xl bg-velvet border-gold/20 p-2">
          <DialogTitle className="sr-only">{light?.name || "Gallery image"}</DialogTitle>
          {light && (light.type === "video"
            ? <video src={fileUrl(light.image)} controls autoPlay className="w-full rounded-lg" />
            : <img src={fileUrl(light.image)} alt={light.name} className="w-full max-h-[80vh] object-contain rounded-lg" />)}
          <p className="text-center text-ivory/70 font-serif text-xl py-3">{light?.name}</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
